import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { MemberDetails } from '~/models/MemberDetails';
import { VStack } from '~/components/gluestack/vstack';
import { HStack } from '~/components/gluestack/hstack';
import { Text } from '~/components/gluestack/text';
import { Heading } from '~/components/gluestack/heading';
import { Badge, BadgeText } from '~/components/gluestack/badge';
import { PresenceIndicator } from '../members/PresenceIndicator';
import { LocationWithFlagRow } from '~/components/ui/LocationWithFlagRow';
import { getCountryName } from '~/hooks/useCountryName';
import { MemberCountLink } from '~/components/domain/common/MemberCountLink';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';
import { Orbit01Icon } from '@hugeicons-pro/core-solid-rounded';
import { SocialTagIcon } from '~/components/domain/common/SocialTagIcon';
import { getSocialTagColor, getContrastTextColor } from '~/constants/socialTagIcons';
import { useCommunity } from '~/hooks/useCommunity';
import { type CommunityCode } from '~/constants/communities';

type DomainSection = string;

interface ProfileOverviewProps {
  member: MemberDetails;
  title: string;
}

export function ProfileOverview({ member, title }: ProfileOverviewProps) {
  const { t } = useTranslation(['default', 'domain']);
  const community = useCommunity();
  const communityCode = community?.code as CommunityCode | undefined;

  // Helper function to format a single item with domain translation
  const formatDomainItem = (value: string | undefined, section: DomainSection): string => {
    if (!value) return '';
    return t(`domain:${section}.${value}`, value);
  };

  return (
    <VStack className="native:gap-1 web:gap-0 grow shrink min-w-0">
      <HStack className="flex-nowrap items-baseline gap-2">
        <Heading size="xl" className="flex-shrink-0 whitespace-nowrap">
          {title}
        </Heading>
        <PresenceIndicator member={member} className="flex-shrink-0 self-center" />
        {!member.IsAdmin && member.Age && (
          <Text size="xl" className="flex-shrink-0 whitespace-nowrap">
            {member.Age}
          </Text>
        )}
        {member.FirstNameCleaned && (
          <Text isTruncated className="min-w-0 flex-1">
            ({member.FirstNameCleaned})
          </Text>
        )}
      </HStack>

      {!member.IsAdmin && (
        <HStack className="items-center gap-3">
          <MemberCountLink
            count={member.FollowersCount}
            label={t('profile.followers', { count: member.FollowersCount })}
            sheetTitle={t('people.followers_short')}
            listType="followers"
            memberId={member.Id}
          />
          <MemberCountLink
            count={member.FollowingCount}
            label={t('profile.following', { count: member.FollowingCount })}
            sheetTitle={t('people.following_short')}
            listType="following"
            memberId={member.Id}
          />

          {member.IsFollower && (
            <Badge size="md" variant="solid" className="mt-0.5 rounded-xl bg-background-900/25 native:bg-background-700 native:dark:bg-background-300">
              <HStack className="items-center gap-1.5">
                <HugeIconsWrapper icon={Orbit01Icon} size={14} className="color-white" />
                <BadgeText className="font-semibold text-white">
                  {t('profile.follows_you', 'Follows you')}
                </BadgeText>
              </HStack>
            </Badge>
          )}
        </HStack>
      )}

      {member.SocialTags && member.SocialTags.length > 0 && (
        <HStack className="mt-2 flex-wrap gap-2">
          {member.SocialTags.map((tag, index) => {
            const bgColor = communityCode ? getSocialTagColor(communityCode, tag) : undefined;
            const textColor = bgColor ? getContrastTextColor(bgColor) : '#ffffff';
            return (
              <Badge
                key={index}
                size="md"
                style={bgColor ? { backgroundColor: bgColor } : undefined}
                className={`rounded-lg ${bgColor ? '' : 'bg-secondary-500'}`}
              >
                <HStack className="items-center gap-1.5">
                  <SocialTagIcon tag={tag} size={16} />
                  <View style={{ width: 1, alignSelf: 'stretch', backgroundColor: `${textColor}50`, borderRadius: 1 }} />
                  <BadgeText style={{ color: textColor }} className="font-semibold">
                    {formatDomainItem(tag, 'ProfileTags.SocialTags')}
                  </BadgeText>
                </HStack>
              </Badge>
            );
          })}
        </HStack>
      )}

      {!member.IsAdmin && (member.PlaceNameCleaned || member.CountryCode) && (
        <LocationWithFlagRow
          className="mt-5"
          mapsQuery={member.PlaceNameCleaned || ''}
          countryCode={member.CountryCode}
          locationText={member.PlaceNameCleaned || getCountryName(member.CountryCode) || member.CountryCode || ''}
          distanceKm={member.DistanceKm}
        />
      )}

    </VStack>
  );
} 